import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ProductRecommendations from "@/components/ProductRecommendations.jsx";
import ProfileLayout from "@/components/ProfileLayout.jsx";
import OrderThumbnail from "@/components/OrderThumbnail.jsx";
import { changePassword, createAddress, deleteAddress, getAddresses, getOrders, getUser, updateAddress, updateUser, uploadCustomerFile } from "@/api/customer.api.js";
import ProductCatalogGrid from "@/components/ProductCatalogGrid.jsx";
import { useFavorites } from "@/favorites/FavoritesContext.jsx";
import { FAVORITES_TAB, useMesFavorisList } from "@/hooks/useMesFavorisList.js";
import { formatOrderStatus, formatPurchaseDate, orderStatusClassName, productImageUrl } from "@/utils/format.js";
import Price from "@/components/Price.jsx";
import { CUSTOMER_TYPES, createEmptyProfile, normalizePhones } from "@/utils/customerProfile.js";
import PhoneFields from "@/components/PhoneFields.jsx";
import RowActionsMenu from "@/components/RowActionsMenu.jsx";
import ConfirmDialog from "@/components/ConfirmDialog.jsx";
import { ORDER_ROUTE } from "@/utils/constants.js";
import { CUSTOMER_PROFILE_DEFAULT_SECTION, CUSTOMER_PROFILE_MENU } from "@/utils/profileMenus.js";
import { shareOrder } from "@/utils/shareOrder.js";
import "./profile.css";

const EMPTY_ADDRESS = {
    label: "",
    streetLine: "",
    city: "",
    region: "",
    country: "Cameroun"
};

export default function CustomerProfile() {
    const navigate = useNavigate();
    const [profile, setProfile] = useState(createEmptyProfile());
    const [photoUploading, setPhotoUploading] = useState(false);
    const [orders, setOrders] = useState([]);
    const [favoritesTab, setFavoritesTab] = useState(FAVORITES_TAB.PURCHASED);
    const [addresses, setAddresses] = useState([]);
    const { refreshFavorites } = useFavorites();
    const [addressForm, setAddressForm] = useState(EMPTY_ADDRESS);
    const [editingAddressId, setEditingAddressId] = useState(null);
    const [passwords, setPasswords] = useState({ oldPassword: "", newPassword: "" });
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [activeSection, setActiveSection] = useState(null);
    const favoritesListEnabled = activeSection === "favorites";
    const {
        products: favoriteProducts,
        loading: favoritesLoading,
        loadingMore: favoritesLoadingMore,
        error: favoritesListError,
        sentinelRef: favoritesSentinelRef,
        hasProducts: hasFavoriteProducts
    } = useMesFavorisList({ tab: favoritesTab, enabled: favoritesListEnabled });
    const [profileEditing, setProfileEditing] = useState(false);
    const [addressFormMode, setAddressFormMode] = useState(null);
    const [passwordEditing, setPasswordEditing] = useState(false);
    const [pendingDeleteAddressId, setPendingDeleteAddressId] = useState(null);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [searchParams, setSearchParams] = useSearchParams();

    const loadData = async () => {
        const [user, ordersResponse, addressResponse] = await Promise.all([
            getUser(),
            getOrders(),
            getAddresses().catch(() => ({ data: [] }))
        ]);
        setProfile({
            ...createEmptyProfile(),
            ...user,
            phones: normalizePhones(user.phones)
        });
        setOrders(ordersResponse.data || []);
        setAddresses(addressResponse.data || []);
    };

    const resetAddressForm = () => {
        setAddressForm(EMPTY_ADDRESS);
        setEditingAddressId(null);
        setAddressFormMode(null);
    };

    const startAddAddress = () => {
        setEditingAddressId(null);
        setAddressForm(EMPTY_ADDRESS);
        setAddressFormMode("add");
        setError("");
    };

    const startEditAddress = (address) => {
        setEditingAddressId(address.id);
        setAddressFormMode(address.id);
        setAddressForm({
            label: address.label || "",
            streetLine: address.streetLine || "",
            city: address.city || "",
            region: address.region || "",
            country: address.country || "Cameroun"
        });
        setError("");
    };

    const saveAddress = async (event) => {
        event.preventDefault();
        try {
            if (editingAddressId) {
                await updateAddress(editingAddressId, addressForm);
                setMessage("Adresse mise à jour");
            } else {
                await createAddress(addressForm);
                setMessage("Adresse ajoutée");
            }
            resetAddressForm();
            setError("");
            await loadData();
        } catch (err) {
            setError(err.message);
        }
    };

    const cancelProfileEdit = async () => {
        setProfileEditing(false);
        setError("");
        try {
            await loadData();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDeleteAddress = async (addressId) => {
        try {
            await deleteAddress(addressId);
            setPendingDeleteAddressId(null);
            if (editingAddressId === addressId) {
                resetAddressForm();
            }
            setMessage("Adresse supprimée");
            setError("");
            await loadData();
        } catch (err) {
            setError(err.message);
        }
    };

    const buildAddressMenuItems = (address) => [
        {
            id: "edit",
            label: "Modifier",
            onClick: () => startEditAddress(address)
        },
        {
            id: "delete",
            label: "Supprimer",
            danger: true,
            onClick: () => setPendingDeleteAddressId(address.id)
        }
    ];

    const activateSection = async (section) => {
        setError("");
        setMessage("");
        setProfileEditing(false);
        setAddressFormMode(null);
        setPasswordEditing(false);
        resetAddressForm();
        setActiveSection(section);
        try {
            await loadData();
            if (section === "favorites") {
                setFavoritesTab(FAVORITES_TAB.PURCHASED);
                await refreshFavorites();
            }
        } catch (err) {
            setError(err.message);
        }
    };

    useEffect(() => {
        const section = searchParams.get("section");
        const resolvedSection = CUSTOMER_PROFILE_MENU.some((item) => item.id === section)
            ? section
            : CUSTOMER_PROFILE_DEFAULT_SECTION;

        if (section !== resolvedSection) {
            setSearchParams({ section: resolvedSection }, { replace: true });
            return;
        }
        if (resolvedSection === activeSection) {
            return;
        }
        activateSection(resolvedSection);
    }, [searchParams, activeSection]);

    const selectSection = (section) => {
        setSearchParams({ section }, { replace: true });
    };

    const saveProfile = async (event) => {
        event.preventDefault();
        try {
            const updated = await updateUser({
                firstName: profile.firstName,
                lastName: profile.lastName,
                email: profile.email?.trim() || null,
                photoId: profile.photoId || undefined,
                companyName: profile.customerType === CUSTOMER_TYPES.CORPORATION ? profile.companyName : undefined,
                registrationNumber: profile.customerType === CUSTOMER_TYPES.CORPORATION ? profile.registrationNumber || undefined : undefined,
                taxId: profile.customerType === CUSTOMER_TYPES.CORPORATION ? profile.taxId || undefined : undefined,
                phones: profile.phones.map((phone) => ({
                    phoneNumber: phone.phoneNumber.trim(),
                    label: phone.label.trim() || undefined
                }))
            });
            setProfile({
                ...updated,
                phones: normalizePhones(updated.phones)
            });
            setProfileEditing(false);
            setMessage("Profil mis à jour");
            setError("");
        } catch (err) {
            setError(err.message);
        }
    };

    const handlePhotoChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }
        setPhotoUploading(true);
        setError("");
        try {
            const response = await uploadCustomerFile(file);
            setProfile((current) => ({ ...current, photoId: response.fileId }));
            setMessage("Photo de profil téléversée");
        } catch (err) {
            setError(err.message);
        } finally {
            setPhotoUploading(false);
            event.target.value = "";
        }
    };

    const savePassword = async (event) => {
        event.preventDefault();
        try {
            await changePassword(passwords);
            setPasswords({ oldPassword: "", newPassword: "" });
            setPasswordEditing(false);
            setMessage("Mot de passe mis à jour");
            setError("");
        } catch (err) {
            setError(err.message);
        }
    };

    const profileTypeLabel = profile.customerType === CUSTOMER_TYPES.CORPORATION ? "Entreprise" : "Particulier";
    const profilePhones = profile.phones?.filter((phone) => phone.phoneNumber?.trim()) || [];
    const pendingDeleteAddress = addresses.find((address) => address.id === pendingDeleteAddressId);

    return (
        <>
            <ProfileLayout
                title="Mon compte"
                menuItems={CUSTOMER_PROFILE_MENU}
                activeSection={activeSection}
                onSelectSection={selectSection}
                message={message}
                error={error}
            >
                {!activeSection && <div className="profile-empty" />}

                {activeSection === "addresses" && (
                    <section className="card profile-section">
                        <div className="section-header">
                            <h2>Mes adresses</h2>
                            {!addressFormMode && (
                                <button type="button" className="btn btn-secondary btn-inline" onClick={startAddAddress}>
                                    Ajouter une adresse
                                </button>
                            )}
                        </div>
                        <p className="muted address-intro">
                            Enregistrez vos adresses (garage, atelier ou autre) pour les utiliser lors de vos commandes.
                        </p>

                        {addresses.length === 0 && !addressFormMode && (
                            <p className="muted">Aucune adresse enregistrée.</p>
                        )}

                        {addresses.length > 0 && !addressFormMode && (
                            <div className="admin-table">
                                {addresses.map((address) => (
                                    <div key={address.id} className="admin-row">
                                        <div className="admin-row-body">
                                            {address.label && <strong>{address.label}</strong>}
                                            <p className="muted">{address.summary}</p>
                                        </div>
                                        <RowActionsMenu
                                            ariaLabel={`Actions pour ${address.label || address.summary}`}
                                            items={buildAddressMenuItems(address)}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}

                        {addressFormMode && (
                            <form onSubmit={saveAddress} className="address-form">
                                <h3>{editingAddressId ? "Modifier l'adresse" : "Ajouter une adresse"}</h3>
                                <label className="field">
                                    Libellé (optionnel)
                                    <input
                                        value={addressForm.label}
                                        onChange={(e) => setAddressForm({ ...addressForm, label: e.target.value })}
                                        placeholder="Maison, Bureau..."
                                    />
                                </label>
                                <label className="field">
                                    Adresse
                                    <input
                                        value={addressForm.streetLine}
                                        onChange={(e) => setAddressForm({ ...addressForm, streetLine: e.target.value })}
                                        required
                                    />
                                </label>
                                <label className="field">
                                    Ville
                                    <input
                                        value={addressForm.city}
                                        onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                                        required
                                    />
                                </label>
                                <label className="field">
                                    Région (optionnel)
                                    <input
                                        value={addressForm.region}
                                        onChange={(e) => setAddressForm({ ...addressForm, region: e.target.value })}
                                    />
                                </label>
                                <label className="field">
                                    Pays
                                    <input
                                        value={addressForm.country}
                                        onChange={(e) => setAddressForm({ ...addressForm, country: e.target.value })}
                                        required
                                    />
                                </label>
                                <div className="profile-form-actions">
                                    <button className="btn btn-primary btn-inline" type="submit">
                                        {editingAddressId ? "Enregistrer" : "Ajouter l'adresse"}
                                    </button>
                                    <button type="button" className="btn btn-secondary btn-inline" onClick={resetAddressForm}>
                                        Annuler
                                    </button>
                                </div>
                            </form>
                        )}
                    </section>
                )}

                {activeSection === "profile" && (
                    <section className="card profile-section">
                        <div className="section-header">
                            <h2>Mon profil</h2>
                            {!profileEditing && (
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-inline"
                                    onClick={() => {
                                        setError("");
                                        setProfileEditing(true);
                                    }}
                                >
                                    Modifier
                                </button>
                            )}
                        </div>

                        {!profileEditing ? (
                            <>
                                <div className="profile-photo-field">
                                    {productImageUrl(profile.photoId) ? (
                                        <img
                                            src={productImageUrl(profile.photoId)}
                                            alt=""
                                            className="profile-photo-preview"
                                        />
                                    ) : (
                                        <div className="profile-photo-preview profile-photo-preview--empty">Photo</div>
                                    )}
                                </div>

                                <dl className="profile-detail-view">
                                    <div className="profile-detail-row">
                                        <dt>Nom d'utilisateur</dt>
                                        <dd>{profile.username || "—"}</dd>
                                    </div>
                                    <div className="profile-detail-row">
                                        <dt>E-mail</dt>
                                        <dd>{profile.email || "—"}</dd>
                                    </div>
                                    <div className="profile-detail-row">
                                        <dt>Type de compte</dt>
                                        <dd>{profileTypeLabel}</dd>
                                    </div>
                                    {profile.customerType === CUSTOMER_TYPES.CORPORATION && (
                                        <>
                                            <div className="profile-detail-row">
                                                <dt>Raison sociale</dt>
                                                <dd>{profile.companyName || "—"}</dd>
                                            </div>
                                            <div className="profile-detail-row">
                                                <dt>N° RCCM</dt>
                                                <dd>{profile.registrationNumber || "—"}</dd>
                                            </div>
                                            <div className="profile-detail-row">
                                                <dt>NIU</dt>
                                                <dd>{profile.taxId || "—"}</dd>
                                            </div>
                                        </>
                                    )}
                                    <div className="profile-detail-row">
                                        <dt>{profile.customerType === CUSTOMER_TYPES.CORPORATION ? "Prénom du contact" : "Prénom"}</dt>
                                        <dd>{profile.firstName || "—"}</dd>
                                    </div>
                                    <div className="profile-detail-row">
                                        <dt>{profile.customerType === CUSTOMER_TYPES.CORPORATION ? "Nom du contact" : "Nom"}</dt>
                                        <dd>{profile.lastName || "—"}</dd>
                                    </div>
                                    <div className="profile-detail-row">
                                        <dt>Numéros de téléphone</dt>
                                        <dd>
                                            {profilePhones.length > 0 ? (
                                                <ul className="profile-phone-view-list">
                                                    {profilePhones.map((phone, index) => (
                                                        <li key={`${phone.phoneNumber}-${index}`}>
                                                            {phone.phoneNumber}
                                                            {phone.label ? ` (${phone.label})` : ""}
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                "—"
                                            )}
                                        </dd>
                                    </div>
                                </dl>
                            </>
                        ) : (
                            <form onSubmit={saveProfile}>
                                <div className="profile-photo-field">
                                    {productImageUrl(profile.photoId) ? (
                                        <img
                                            src={productImageUrl(profile.photoId)}
                                            alt=""
                                            className="profile-photo-preview"
                                        />
                                    ) : (
                                        <div className="profile-photo-preview profile-photo-preview--empty">Photo</div>
                                    )}
                                    <label className="btn btn-secondary btn-inline profile-photo-upload">
                                        {photoUploading ? "Téléversement..." : "Changer la photo"}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handlePhotoChange}
                                            disabled={photoUploading}
                                            hidden
                                        />
                                    </label>
                                </div>

                                <label className="field">
                                    Nom d'utilisateur
                                    <input value={profile.username || ""} disabled />
                                </label>
                                <label className="field">
                                    E-mail (optionnel)
                                    <input
                                        type="email"
                                        value={profile.email || ""}
                                        onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                                        maxLength={255}
                                        autoComplete="email"
                                    />
                                </label>
                                <label className="field">
                                    Type de compte
                                    <input value={profileTypeLabel} disabled />
                                </label>

                                {profile.customerType === CUSTOMER_TYPES.CORPORATION && (
                                    <>
                                        <label className="field">
                                            Raison sociale
                                            <input
                                                value={profile.companyName || ""}
                                                onChange={(e) => setProfile({ ...profile, companyName: e.target.value })}
                                                required
                                            />
                                        </label>
                                        <label className="field">
                                            N° RCCM (optionnel)
                                            <input
                                                value={profile.registrationNumber || ""}
                                                onChange={(e) => setProfile({ ...profile, registrationNumber: e.target.value })}
                                            />
                                        </label>
                                        <label className="field">
                                            NIU (optionnel)
                                            <input
                                                value={profile.taxId || ""}
                                                onChange={(e) => setProfile({ ...profile, taxId: e.target.value })}
                                            />
                                        </label>
                                    </>
                                )}

                                <label className="field">
                                    {profile.customerType === CUSTOMER_TYPES.CORPORATION ? "Prénom du contact" : "Prénom"}
                                    <input
                                        value={profile.firstName || ""}
                                        onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                                        required
                                    />
                                </label>
                                <label className="field">
                                    {profile.customerType === CUSTOMER_TYPES.CORPORATION ? "Nom du contact" : "Nom"}
                                    <input
                                        value={profile.lastName || ""}
                                        onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                                        required
                                    />
                                </label>

                                <PhoneFields
                                    phones={profile.phones}
                                    onChange={(phones) => setProfile({ ...profile, phones })}
                                    idPrefix="profile-phone"
                                />

                                <div className="profile-form-actions">
                                    <button className="btn btn-primary btn-inline" type="submit">Enregistrer le profil</button>
                                    <button type="button" className="btn btn-secondary btn-inline" onClick={cancelProfileEdit}>
                                        Annuler
                                    </button>
                                </div>
                            </form>
                        )}
                    </section>
                )}

                {activeSection === "password" && (
                    <section className="card profile-section">
                        <div className="section-header">
                            <h2>Changer le mot de passe</h2>
                            {!passwordEditing && (
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-inline"
                                    onClick={() => {
                                        setError("");
                                        setPasswordEditing(true);
                                    }}
                                >
                                    Modifier
                                </button>
                            )}
                        </div>

                        {!passwordEditing ? (
                            <p className="muted">Votre mot de passe est masqué pour des raisons de sécurité.</p>
                        ) : (
                            <form onSubmit={savePassword}>
                                <label className="field">
                                    Mot de passe actuel
                                    <div className="password-field">
                                        <input
                                            type={showOldPassword ? "text" : "password"}
                                            value={passwords.oldPassword}
                                            onChange={(e) => setPasswords({ ...passwords, oldPassword: e.target.value })}
                                            required
                                        />
                                        <button type="button" className="password-toggle" onClick={() => setShowOldPassword(!showOldPassword)}>
                                            {showOldPassword ? "Masquer" : "Afficher"}
                                        </button>
                                    </div>
                                </label>
                                <label className="field">
                                    Nouveau mot de passe
                                    <div className="password-field">
                                        <input
                                            type={showNewPassword ? "text" : "password"}
                                            value={passwords.newPassword}
                                            onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                                            required
                                        />
                                        <button type="button" className="password-toggle" onClick={() => setShowNewPassword(!showNewPassword)}>
                                            {showNewPassword ? "Masquer" : "Afficher"}
                                        </button>
                                    </div>
                                </label>
                                <div className="profile-form-actions">
                                    <button className="btn btn-primary btn-inline" type="submit">Enregistrer</button>
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-inline"
                                        onClick={() => {
                                            setPasswordEditing(false);
                                            setPasswords({ oldPassword: "", newPassword: "" });
                                            setError("");
                                        }}
                                    >
                                        Annuler
                                    </button>
                                </div>
                            </form>
                        )}
                    </section>
                )}

                {activeSection === "purchases" && (
                    <section className="card profile-section profile-section--wide">
                        <h2>Mes achats</h2>
                        {orders.length > 0 && (
                            <p className="muted admin-list-hint">
                                {orders.length} commande{orders.length > 1 ? "s" : ""} · ouvrez une ligne ou le menu ⋮ pour le détail
                            </p>
                        )}
                        {orders.length === 0 ? (
                            <p className="muted">Aucune commande pour le moment.</p>
                        ) : (
                            <div className="admin-table purchase-list">
                                {orders.map((order) => {
                                    const orderLabel = order.previewTitle || "Commande";
                                    const openOrder = () => navigate(ORDER_ROUTE(order.orderId));
                                    const handleShare = async () => {
                                        setError("");
                                        try {
                                            const result = await shareOrder({
                                                orderId: order.orderId,
                                                orderDate: order.orderDate
                                            });
                                            if (result === "copied") {
                                                setMessage("Lien de la commande copié");
                                            }
                                        } catch {
                                            setError("Partage indisponible");
                                        }
                                    };
                                    return (
                                        <div key={order.orderId} className="admin-row admin-list-row purchase-list-row">
                                            <button
                                                type="button"
                                                className="admin-list-row-main purchase-list-row-main"
                                                onClick={openOrder}
                                            >
                                                <div className="purchase-list-row-top">
                                                    <OrderThumbnail order={order} />
                                                    <div className="purchase-list-row-heading">
                                                        <strong>{orderLabel}</strong>
                                                        <p className="muted">
                                                            {order.itemCount} article{order.itemCount > 1 ? "s" : ""}
                                                            {" · "}
                                                            <span className={orderStatusClassName(order.status)}>
                                                                {formatOrderStatus(order.status)}
                                                            </span>
                                                        </p>
                                                    </div>
                                                    <span className="admin-list-row-chevron" aria-hidden="true">›</span>
                                                </div>
                                                {order.deliverySummary && (
                                                    <p className="muted purchase-fulfillment">{order.deliverySummary}</p>
                                                )}
                                                <p className="muted purchase-row-date">
                                                    <Price value={order.totalPrice} />
                                                    {" · "}
                                                    {formatPurchaseDate(order.orderDate)}
                                                </p>
                                            </button>
                                            <RowActionsMenu
                                                ariaLabel={`Actions pour ${orderLabel}`}
                                                items={[
                                                    {
                                                        id: "view",
                                                        label: "Voir les détails",
                                                        onClick: openOrder
                                                    },
                                                    {
                                                        id: "share",
                                                        label: "Partager",
                                                        onClick: handleShare
                                                    }
                                                ]}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        <ProductRecommendations className="profile-recommendations" />
                    </section>
                )}

                {activeSection === "favorites" && (
                    <section className="card profile-section profile-section--wide">
                        <h2>Mes Favoris</h2>
                        <div className="favorites-tabs" role="tablist" aria-label="Mes Favoris">
                            <button
                                type="button"
                                role="tab"
                                className={`favorites-tab${favoritesTab === FAVORITES_TAB.PURCHASED ? " is-active" : ""}`}
                                aria-selected={favoritesTab === FAVORITES_TAB.PURCHASED}
                                onClick={() => setFavoritesTab(FAVORITES_TAB.PURCHASED)}
                            >
                                Vous avez acheté
                            </button>
                            <button
                                type="button"
                                role="tab"
                                className={`favorites-tab${favoritesTab === FAVORITES_TAB.LIKED ? " is-active" : ""}`}
                                aria-selected={favoritesTab === FAVORITES_TAB.LIKED}
                                onClick={() => setFavoritesTab(FAVORITES_TAB.LIKED)}
                            >
                                J&apos;aime
                            </button>
                        </div>
                        {favoritesLoading && <p className="muted">Chargement...</p>}
                        {!favoritesLoading && favoritesListError && <p className="error">{favoritesListError}</p>}
                        {!favoritesLoading && !favoritesListError && !hasFavoriteProducts && (
                            <p className="muted">
                                {favoritesTab === FAVORITES_TAB.LIKED
                                    ? "Aucun produit dans « J'aime » pour le moment."
                                    : "Aucun produit acheté pour le moment."}
                            </p>
                        )}
                        {!favoritesLoading && !favoritesListError && hasFavoriteProducts && (
                            <>
                                {favoritesTab === FAVORITES_TAB.LIKED && (
                                    <p className="muted admin-list-hint">
                                        {favoriteProducts.length} produit{favoriteProducts.length > 1 ? "s" : ""}
                                        {" · "}cliquez sur le cœur pour retirer un article
                                    </p>
                                )}
                                {favoritesTab === FAVORITES_TAB.PURCHASED && (
                                    <p className="muted admin-list-hint">
                                        {favoriteProducts.length} produit{favoriteProducts.length > 1 ? "s" : ""}
                                    </p>
                                )}
                                <ProductCatalogGrid products={favoriteProducts} />
                            </>
                        )}
                        {favoritesLoadingMore && <p className="muted catalog-loading-more">Chargement de la suite...</p>}
                        <div ref={favoritesSentinelRef} className="catalog-scroll-sentinel" aria-hidden="true" />
                        <ProductRecommendations className="profile-recommendations" />
                    </section>
                )}
            </ProfileLayout>
            <ConfirmDialog
                open={!!pendingDeleteAddressId}
                title="Supprimer l'adresse"
                message={
                    pendingDeleteAddress
                        ? `Voulez-vous vraiment supprimer « ${pendingDeleteAddress.label || pendingDeleteAddress.summary} » ? Cette action est irréversible.`
                        : "Voulez-vous vraiment supprimer cette adresse ? Cette action est irréversible."
                }
                confirmLabel="Supprimer"
                dangerous
                onConfirm={() => handleDeleteAddress(pendingDeleteAddressId)}
                onCancel={() => setPendingDeleteAddressId(null)}
            />
        </>
    );
}
